import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';

const SubjectsManagement = () => {
  const { getAccessToken } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subjects`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubjects(data);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;

    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subjects`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: newSubject })
        }
      );

      if (response.ok) {
        await fetchSubjects();
        setNewSubject('');
        alert('Subject added successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to add subject: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to add subject:', error);
      alert('Failed to add subject');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    if (!window.confirm(`Are you sure you want to delete "${subjectName}"?`)) return;

    try {
      const token = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subjects/${subjectId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        await fetchSubjects();
        alert('Subject deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to delete subject: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to delete subject:', error);
      alert('Failed to delete subject');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Manage Subjects</h2>
        
        {/* Add Subject Form */}
        <form onSubmit={handleAddSubject} className="mb-6">
          <div className="flex gap-3">
            <Input
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Enter new subject name (e.g., Mathematics)"
              className="flex-1"
              required
            />
            <Button type="submit" disabled={loading} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Subject
            </Button>
          </div>
        </form>

        {/* Subjects List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjects.map((subject) => (
            <div
              key={subject.id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors"
            >
              <span className="font-medium text-gray-900 dark:text-white">{subject.name}</span>
              <button
                onClick={() => handleDeleteSubject(subject.id, subject.name)}
                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
              </button>
            </div>
          ))}
        </div>

        {subjects.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No subjects added yet. Add your first subject above.
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectsManagement;
